import {ConnectionInfo} from "../../types/types";
import * as openpgp from 'openpgp';
import {v4} from "uuid";

let ws = null
let encryptionEnabled = false

type Callbacks = {
    connected: (() => void)[]
    loggedIn: ((connectionInfo: ConnectionInfo) => void)[]
    onMessage: ((action: string, data: { [key: string]: any }) => void)[]
    swRequest: ((action: string, data: { [key: string]: any }, reply: (data: any) => void) => void)[]
}

let callbacks: Callbacks = {
    connected: [],
    loggedIn: [],
    onMessage: [],
    swRequest: [],
}

export type WsActions = {
    pluginUpdate: (id: string, config: { [key: string]: any }) => void
    pluginAdd: (name: string) => void
    pluginRemove: (id: string) => void
    pluginTestAction(pluginName: string, pluginId: string, actionKey: string, action: string, config: {}): void;
}

export type WSClientFunctions = {
    wsSend: (action: string, data?: { [p: string]: any }) => Promise<void>;
    onMessage: (callback: (action: string, data: { [p: string]: any }) => void) => number;
    // @ts-ignore
    requestFromSW: (requestId: string, room: string, requestAction: string, data?: any, onReply: (reply: any) => void) => void
    onAdminRequest: (callback: (action: string, data: { [p: string]: any }, reply: (data: any) => void) => void) => number;
    onLoggedIn: (callback: (connectionInfo: ConnectionInfo) => void) => number;
    wsConnect: (token: string) => Promise<unknown>;
    onConnected: (callback: () => void) => number;
    wsActions: WsActions
}

let keys
let privateKeyObj
let serverPublicKey

// @ts-ignore
let pendingSwReplies: { requestId: string, onReply: (reply: any) => void } = {}

export const useWsClient = (clientType: string, autoLogin: boolean = true): WSClientFunctions => {

    const wsSend = async (action: string, data: { [key: string]: any } = {}) => {
        if (ws) {
            let payload = JSON.stringify({
                action,
                ...data
            })

            if (action !== 'login' && encryptionEnabled && serverPublicKey) {
                payload = await openpgp.encrypt({
                    message: await openpgp.createMessage({ text: payload }), // input as Message object
                    encryptionKeys: serverPublicKey,
                });
            }

            ws.send(payload)
        }
    }

    const requestFromSW = (requestId: string, room: string, requestAction: string, data: any = {}, onReply: (reply: any) => void) => {
        pendingSwReplies[requestId] = onReply

        wsSend('request-from-sw', {
            requestId,
            room,
            requestAction,
            data,
        })
    }

    const wsConnect = async (token: string) => {
        if (!keys) {
            const passphrase = v4()

            const { publicKey, privateKey } = await openpgp.generateKey({
                userIDs: [{ name: token, email: `${token}@${location.hostname}` }],
                // @ts-ignore
                curve: 'ed25519',
                passphrase,
            });
            keys = {
                publicKey,
                privateKey,
            }

            privateKeyObj = await openpgp.decryptKey({
                privateKey: await openpgp.readPrivateKey({
                    armoredKey: keys.privateKey,
                }),
                passphrase,
            });
        }

        console.log('WS CLIENT', 'connect', token)
        return new Promise(resolve => {
            // @ts-ignore
            ws = new WebSocket(process.env.BACKEND_URL)

            ws.onopen = () => {
                if (autoLogin) {
                    console.log('reconnected', 'logging in')
                    wsSend(`login`, {
                        type: clientType,
                        url: location.href,
                        token,
                        pubKey: keys.publicKey,
                    })
                } else {
                    console.log('reconnected', 'auto-login disabled')
                }
                callbacks.connected.forEach(cb => cb())
            }

            ws.onclose = () => {
                setTimeout(() => wsConnect(token), 2000)
            }

            ws.onerror = error => {
                console.error('connection error', error, ' reconnect')
                // connect()
            }

            ws.onmessage = async message => {
                let payload = message.data

                if (payload.substring(0, 27) === '-----BEGIN PGP MESSAGE-----') {
                    encryptionEnabled = true
                    try {
                        const { data: decrypted } = await openpgp.decrypt({
                            message: await openpgp.readMessage({
                                armoredMessage: payload,
                            }),
                            format: 'utf8',
                            decryptionKeys: privateKeyObj,
                        });
                        payload = decrypted
                    } catch (e: any) {
                        console.log('decrypt error', e)
                    }
                } else {
                    encryptionEnabled = false
                }

                const data = JSON.parse(payload)

                if (typeof data.action === 'undefined') {
                    console.error('got invalid message', message)
                    return
                }

                if (data.action === 'logged-in') {
                    if (typeof data.publicKey !== 'undefined') {
                        serverPublicKey = await openpgp.readKey({ armoredKey: data.publicKey })
                        delete data.publicKey
                    }
                    callbacks.loggedIn.forEach(cb => cb(data))
                    resolve(null)
                } else if (data.action === 'admin-request') {
                    callbacks.swRequest.forEach(cb => {
                        cb(
                            data.requestAction,
                            data.data,
                            (reply) => {
                                wsSend('admin-reply', {
                                    requestId: data.requestId,
                                    reply,
                                })
                            }
                        )
                    })
                } else if (data.action === 'reply-from-sw') {
                    if (typeof pendingSwReplies[data.requestId] !== 'undefined') {
                        pendingSwReplies[data.requestId](data.reply)
                        delete pendingSwReplies[data.requestId]
                    }
                } else {
                    const action = data.action || 'unknown'
                    if (typeof data.action !== 'undefined') {
                        delete data.action
                    }
                    callbacks.onMessage.forEach(cb => cb(action, data))
                }
            }
        })
    }

    const wsActions: WsActions = {
        pluginUpdate: (id, config) => wsSend('plugin-update', {
            id,
            config,
        }),
        pluginAdd: (name) => wsSend('plugin-add', {
            name,
        }),
        pluginRemove: (id) => wsSend('plugin-remove', {
            id,
        }),
        pluginTestAction: (plugin, id, actionKey, button, data) => wsSend('plugin-test-action', {
            plugin,
            id,
            actionKey,
            button,
            data
        })
    }

    return {
        wsConnect,
        wsSend,
        requestFromSW,
        onConnected: (callback: () => void) => callbacks.connected.push(callback),
        onLoggedIn: (callback: (connectionInfo: ConnectionInfo) => void) => callbacks.loggedIn.push(callback),
        onMessage: (callback: (action: string, data: { [key: string]: any }) => void) => callbacks.onMessage.push(callback),
        onAdminRequest: (callback: (action: string, data: { [key: string]: any }, reply: (data: any) => void) => void) => callbacks.swRequest.push(callback),
        wsActions,
    }
}
